import React, { useCallback, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { ArrowIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { PopupDynamicFieldView } from 'constants/formBuilder';
import { getDynamicFieldPath } from 'utils/forms';

import { formsTestIds } from '../../../forms.testIds';
import DynamicFieldBreadcrumb from '../DynamicFieldBreadcrumb';
import {
  backButtonContentStyles,
  backButtonIconStyles,
  backButtonStyles,
  editOptionsScreenStyles,
  footerStyles,
  formContainerStyles,
  optionsGridStyles,
} from '../DynamicFieldOptions.styles';

import { defaultChosenOption } from './DynamicFieldOptionsLayout.constants';

const DynamicFieldOptionsLayout = ({
  dataTestId,
  chosenOption: [title = '', value = '', , label = ''] = defaultChosenOption,
  editor,
  setParentView,
  onSubmit,
  onClose,
  isSubmitDisabled = false,
  children,
}) => {
  const dynamicFieldsSchema = editor?.config?.get('dynamicFieldConfig')?.dynamicFieldsSchema;

  const breadcrumbPath = useMemo(
    () => getDynamicFieldPath({ dynamicFieldsSchema, value, title, label }),
    [dynamicFieldsSchema, value, title, label],
  );

  const onGoToList = useCallback(() => {
    setParentView(PopupDynamicFieldView.FIELDS_LIST_VIEW);
  }, [setParentView]);

  return (
    <Box data-testid={dataTestId} sx={editOptionsScreenStyles}>
      <Box component="button" sx={backButtonStyles} type="button" onClick={onGoToList}>
        <Typography color="textPrimary" sx={backButtonContentStyles} variant="regular14">
          <ArrowIcon
            direction={IconDirection.LEFT}
            size={IconSize[16]}
            style={backButtonIconStyles}
          />
          Go back to the list
        </Typography>
      </Box>

      <DynamicFieldBreadcrumb path={breadcrumbPath} />

      <Box component="form" sx={formContainerStyles} onSubmit={onSubmit}>
        <Box sx={optionsGridStyles}>{children}</Box>

        <Box sx={footerStyles}>
          <Button
            data-testid={formsTestIds.DYNAMIC_FIELD_OPTIONS_LAYOUT_CANCEL_BUTTON}
            type="button"
            variant="contained"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            data-testid={formsTestIds.DYNAMIC_FIELD_OPTIONS_LAYOUT_SAVE_BUTTON}
            disabled={isSubmitDisabled}
            type="submit"
            variant="contained"
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

DynamicFieldOptionsLayout.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  chosenOption: PropTypes.array.isRequired,
  editor: PropTypes.object.isRequired,
  setParentView: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isSubmitDisabled: PropTypes.bool,
};

export default DynamicFieldOptionsLayout;
